import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  CreditCard,
  Plus,
  RefreshCw,
  TrendingDown,
  Zap,
  Target,
  Calendar,
  Clock,
  DollarSign,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Sparkles,
  Info,
  BarChart3,
  Wallet,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import './PaymentPlanner.css';

const PP_NS = 'creditBuilder.paymentPlanner';

const normalizeSourceKey = (source) => {
  if (!source) return '';
  const s = String(source).trim().toLowerCase();
  if (s === 'imported') return 'imported';
  if (s === 'manual') return 'manual';
  return '';
};

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ===================== HELPER FUNCTIONS =====================
const formatCurrency = (amount, decimals = 0) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount || 0);
};

const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(decimals)}%`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

// Strategy icons mapping
const STRATEGY_ICONS = {
  avalanche: TrendingDown,
  snowball: Target,
  highest_balance: Wallet,
  highest_utilization: BarChart3,
  statement_date: Calendar,
  due_date: Clock,
};

// ===================== STRATEGY SELECTOR =====================
const StrategySelector = ({ strategies, selected, onSelect }) => {
  const { t } = useI18n();
  const strategyField = (key, field) =>
    t(`${PP_NS}.strategies.${key}.${field}`, null, strategies[key]?.[field] ?? '');

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2 lg:gap-3">
      {Object.entries(strategies).map(([key]) => {
        const Icon = STRATEGY_ICONS[key] || Target;
        const isActive = selected === key;

        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className={`p-2.5 lg:p-3.5 rounded-[14px] lg:rounded-[18px] text-left transition-all duration-200 shadow-[0_8px_22px_rgba(21,46,31,0.04)] ${
              isActive ? 'pp-strategy-tile-active' : 'pp-strategy-tile'
            }`}
            data-testid={`strategy-${key}`}
          >
            <div className="flex items-center gap-1.5 lg:gap-2 mb-1 lg:mb-1.5">
              <Icon size={14} className={`lg:w-4 lg:h-4 pp-strategy-icon ${isActive ? 'pp-strategy-tile-active' : ''}`} />
              <span className="text-xs lg:text-sm font-extrabold pp-strategy-label">
                {strategyField(key, 'label')}
              </span>
            </div>
            <p className="text-[10px] lg:text-xs pp-muted leading-tight line-clamp-2">{strategyField(key, 'subtitle')}</p>
          </button>
        );
      })}
    </div>
  );
};

// ===================== SUMMARY CARD =====================
const SummaryCard = ({ label, value, subtitle, variant = 'default' }) => {
  const valueColor = {
    default: 'text-[#15221a]',
    green: 'text-[#0f7a49]',
    gold: 'text-[#b58933]',
    red: 'text-[#e25b5b]',
  }[variant];
  
  return (
    <div className="p-3 lg:p-4 rounded-[16px] lg:rounded-[20px] border border-[#dbe8df] bg-white shadow-[0_8px_22px_rgba(21,46,31,0.04)]">
      <div className="text-[10px] lg:text-xs text-[#5f7468] font-semibold mb-1.5 lg:mb-2.5">{label}</div>
      <div className={`text-lg lg:text-2xl font-extrabold tracking-tight ${valueColor} truncate`}>{value}</div>
      {subtitle && <div className="text-[10px] lg:text-xs text-[#8a9b91] mt-1.5 lg:mt-2 leading-relaxed line-clamp-2">{subtitle}</div>}
    </div>
  );
};

// ===================== AI RECOMMENDATION PANEL =====================
const AIRecommendationPanel = ({ recommendation, strategies, selectedStrategy }) => {
  const { t } = useI18n();
  const PP = PP_NS;
  if (!recommendation) return null;

  const strategyField = (key, field) =>
    t(`${PP}.strategies.${key}.${field}`, null, strategies[key]?.[field] ?? '');
  const bestStrategyLabel = strategyField(recommendation.best, 'label');
  const selectedStrategyLabel = strategyField(selectedStrategy, 'label');
  
  return (
    <div className="p-[18px] rounded-[24px] bg-gradient-to-br from-[#f4fbf7] to-[#fbf8ef] border border-[#d8e8dc] shadow-[0_12px_32px_rgba(21,46,31,0.06)]">
      <div className="flex justify-between items-start gap-3 flex-wrap mb-3.5">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-[#f7f1e3] text-[#9c7830] text-xs font-extrabold border border-[#ecdcb7] mb-2">
            <Sparkles size={14} />
            {t(`${PP}.aiRecommendation`)}
          </div>
          <h2 className="text-lg font-extrabold pp-heading">
            {t(`${PP}.bestStrategy`, { strategy: bestStrategyLabel || recommendation.best })}
          </h2>
          <p className="text-[13px] pp-muted mt-1.5 leading-relaxed">
            {t(`${PP}.selectedStrategyCompare`, { selected: selectedStrategyLabel })}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-2.5 py-2 rounded-full bg-[#e4f5eb] text-[#0f7a49] text-xs font-extrabold border border-[#cfe8d8]">
          <CheckCircle size={14} />
          {t(`${PP}.confidencePct`, { percent: recommendation.confidence }, `Confidence • ${recommendation.confidence}%`)}
        </div>
      </div>
      
      <div className="grid gap-3">
        <div className="p-3.5 rounded-[18px] bg-white/75 border border-[#e0ebe3]">
          <div className="text-[11px] uppercase tracking-wider text-[#8a9b91] font-extrabold mb-2">{t(`${PP}.whyMethodWins`)}</div>
          <p className="text-sm text-[#15221a] leading-relaxed">{recommendation.why}</p>
        </div>
        <div className="p-3.5 rounded-[18px] bg-white/75 border border-[#e0ebe3]">
          <div className="text-[11px] uppercase tracking-wider text-[#8a9b91] font-extrabold mb-2">{t(`${PP}.thisMonthsMove`)}</div>
          <p className="text-sm text-[#15221a] leading-relaxed">{recommendation.move}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="p-3.5 rounded-[18px] bg-white/75 border border-[#e0ebe3]">
            <div className="text-[11px] uppercase tracking-wider text-[#8a9b91] font-extrabold mb-2">{t(`${PP}.dataWatchlist`)}</div>
            <p className="text-sm text-[#15221a] leading-relaxed">{recommendation.watch}</p>
          </div>
          <div className="p-3.5 rounded-[18px] bg-white/75 border border-[#e0ebe3]">
            <div className="text-[11px] uppercase tracking-wider text-[#8a9b91] font-extrabold mb-2">{t(`${PP}.alternateOption`)}</div>
            <p className="text-sm text-[#15221a] leading-relaxed">{recommendation.alt_text}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== ACCOUNT CARD =====================
const AccountCard = ({ account, rank, strategyKey }) => {
  const { t } = useI18n();
  const PP = 'creditBuilder.paymentPlanner';
  const util = account.utilization;
  const sourceKey = normalizeSourceKey(account.source);
  const isImported = sourceKey === 'imported';
  const sourceLabel = sourceKey
    ? t(`${PP}.accountSource.${sourceKey}`)
    : account.source;
  
  // Status chips based on missing data
  const missingAPR = account.apr === null && strategyKey === 'avalanche';
  const missingStmt = !account.statement_date && strategyKey === 'statement_date';
  const missingUtil = util === null && strategyKey === 'highest_utilization';
  
  return (
    <article className="p-3 lg:p-4 rounded-[18px] lg:rounded-[22px] bg-white border border-[#dbe8df] shadow-[0_8px_22px_rgba(21,46,31,0.04)]">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3 lg:gap-4">
        <div className="flex gap-2.5 lg:gap-3.5 items-start">
          <div className="w-10 h-10 lg:w-[46px] lg:h-[46px] rounded-[12px] lg:rounded-[15px] grid place-items-center bg-gradient-to-br from-[#e6f5ec] to-[#f9f3e5] text-[#0f7a49] text-[13px] lg:text-[15px] font-black border border-[#d3e8da] shrink-0">
            #{rank}
          </div>
          <div className="min-w-0">
            <h3 className="text-base lg:text-lg font-extrabold text-[#15221a] truncate">{account.account_name}</h3>
            <div className="flex gap-1.5 lg:gap-2 flex-wrap mt-1 lg:mt-1.5">
              <span className={`px-2 lg:px-2.5 py-1 lg:py-1.5 rounded-full text-[10px] lg:text-xs font-bold border ${
                isImported 
                  ? 'bg-[#e4f5eb] text-[#0f7a49] border-[#cfe6d8]'
                  : 'bg-[#f7f1e3] text-[#9c7830] border-[#ecdcb7]'
              }`}>
                {sourceLabel}
              </span>
              <span className="px-2 lg:px-2.5 py-1 lg:py-1.5 rounded-full text-[10px] lg:text-xs font-bold border border-[#dbe8df] bg-[#f8fbf9] text-[#5f7468] truncate max-w-[120px]">
                {account.issuer_name || t(`${PP}.issuerNotSet`)}
              </span>
              {missingAPR && (
                <span className="px-2 lg:px-2.5 py-1 lg:py-1.5 rounded-full text-[10px] lg:text-xs font-bold border border-[#f3d1d1] bg-[#fce9e9] text-[#b24747]">
                  {t(`${PP}.aprNeeded`)}
                </span>
              )}
              {missingStmt && (
                <span className="px-2 lg:px-2.5 py-1 lg:py-1.5 rounded-full text-[10px] lg:text-xs font-bold border border-[#f3d1d1] bg-[#fce9e9] text-[#b24747]">
                  {t(`${PP}.statementMissing`)}
                </span>
              )}
              {missingUtil && (
                <span className="px-2 lg:px-2.5 py-1 lg:py-1.5 rounded-full text-[10px] lg:text-xs font-bold border border-[#f3d1d1] bg-[#fce9e9] text-[#b24747]">
                  {t(`${PP}.limitNeeded`)}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-left sm:text-right w-full sm:w-auto">
          <div className="text-xl lg:text-[26px] font-black tracking-tight text-[#15221a]">{formatCurrency(account.balance)}</div>
          <div className="text-[10px] lg:text-xs text-[#8a9b91] mt-1 lg:mt-1.5">{t(`${PP}.currentBalance`)}</div>
        </div>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-2.5 mt-3 lg:mt-3.5">
        <div className="p-2 lg:p-3 rounded-[12px] lg:rounded-[16px] bg-[#f8fbf9] border border-[#e4eee8]">
          <div className="text-[9px] lg:text-[11px] uppercase tracking-wider text-[#8a9b91] font-bold mb-1 lg:mb-1.5">{t(`${PP}.aprFieldLabel`)}</div>
          <div className="text-xs lg:text-sm font-extrabold text-[#15221a]">
            {account.apr !== null ? formatPercent(account.apr, 2) : <span className="text-[#8a9b91]">{t(`${PP}.missing`)}</span>}
          </div>
        </div>
        <div className="p-2 lg:p-3 rounded-[12px] lg:rounded-[16px] bg-[#f8fbf9] border border-[#e4eee8]">
          <div className="text-[9px] lg:text-[11px] uppercase tracking-wider text-[#8a9b91] font-bold mb-1 lg:mb-1.5">{t(`${PP}.utilization`)}</div>
          <div className="text-xs lg:text-sm font-extrabold text-[#15221a]">
            {util !== null ? formatPercent(util) : <span className="text-[#8a9b91]">{t(`${PP}.na`)}</span>}
          </div>
        </div>
        <div className="p-2 lg:p-3 rounded-[12px] lg:rounded-[16px] bg-[#f8fbf9] border border-[#e4eee8]">
          <div className="text-[9px] lg:text-[11px] uppercase tracking-wider text-[#8a9b91] font-bold mb-1 lg:mb-1.5">{t(`${PP}.minimum`)}</div>
          <div className="text-xs lg:text-sm font-extrabold text-[#15221a]">
            {account.minimum ? formatCurrency(account.minimum) : <span className="text-[#8a9b91]">—</span>}
          </div>
        </div>
        <div className="p-2 lg:p-3 rounded-[12px] lg:rounded-[16px] bg-[#f8fbf9] border border-[#e4eee8]">
          <div className="text-[9px] lg:text-[11px] uppercase tracking-wider text-[#8a9b91] font-bold mb-1 lg:mb-1.5">{t(`${PP}.extra`)}</div>
          <div className="text-xs lg:text-sm font-extrabold text-[#0f7a49]">{formatCurrency(account.extra)}</div>
        </div>
        <div className="p-2 lg:p-3 rounded-[12px] lg:rounded-[16px] bg-[#f8fbf9] border border-[#e4eee8] col-span-2 sm:col-span-1">
          <div className="text-[9px] lg:text-[11px] uppercase tracking-wider text-[#8a9b91] font-bold mb-1 lg:mb-1.5">{t(`${PP}.dates`)}</div>
          <div className="text-xs lg:text-sm font-extrabold text-[#15221a] leading-relaxed">
            {t(`${PP}.dueDateLabel`, { date: formatDate(account.due_date) }, `Due ${formatDate(account.due_date)}`)}
            <span className="text-[#8a9b91] font-semibold"> • {t(`${PP}.stmtDateLabel`, { date: formatDate(account.statement_date) }, `Stmt ${formatDate(account.statement_date)}`)}</span>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 lg:gap-3 mt-3 lg:mt-3.5">
        <div className="flex-1 p-2.5 lg:p-3 rounded-[12px] lg:rounded-[16px] bg-[#edf8f1] border border-[#d6eadb] text-[#145639] text-[11px] lg:text-[13px] leading-relaxed font-semibold">
          {account.reason}
        </div>
        <div className="px-2.5 lg:px-3 py-2 lg:py-2.5 rounded-[12px] lg:rounded-[16px] bg-[#fff8ea] border border-[#eeddb5] text-[#9c7830] text-[11px] lg:text-[13px] font-extrabold whitespace-nowrap text-center">
          {t(`${PP}.totalPaymentArrow`, { total: formatCurrency(account.total_payment), after: formatCurrency(account.balance_after) }, `Total ${formatCurrency(account.total_payment)} → ${formatCurrency(account.balance_after)}`)}
        </div>
      </div>
    </article>
  );
};

// ===================== ADD ACCOUNT MODAL =====================
const AddAccountModal = ({ isOpen, onClose, onSave }) => {
  const { t } = useI18n();
  const PP = 'creditBuilder.paymentPlanner';
  const [formData, setFormData] = useState({
    account_name: '',
    issuer_name: '',
    balance: '',
    apr: '',
    minimum_payment: '',
    limit: '',
    due_date: '',
    statement_date: '',
    account_type: 'credit_card',
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSave = async () => {
    if (!formData.account_name || !formData.balance) {
      toast.error(t(`${PP}.enterNameAndBalance`));
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave({
        account_name: formData.account_name,
        issuer_name: formData.issuer_name || null,
        balance: parseFloat(formData.balance) || 0,
        apr: formData.apr ? parseFloat(formData.apr) : null,
        minimum_payment: formData.minimum_payment ? parseFloat(formData.minimum_payment) : null,
        limit: formData.limit ? parseFloat(formData.limit) : null,
        due_date: formData.due_date || null,
        statement_date: formData.statement_date || null,
        account_type: formData.account_type,
        notes: formData.notes || null,
      });
      setFormData({
        account_name: '',
        issuer_name: '',
        balance: '',
        apr: '',
        minimum_payment: '',
        limit: '',
        due_date: '',
        statement_date: '',
        account_type: 'credit_card',
        notes: '',
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Plus size={18} className="sm:w-5 sm:h-5 text-[#18a563]" />
            {t(`${PP}.modalTitle`)}
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 py-3 sm:py-4">
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.accountNameLabel`)}</Label>
            <Input
              value={formData.account_name}
              onChange={(e) => setFormData(prev => ({ ...prev, account_name: e.target.value }))}
              placeholder={t(`${PP}.placeholderAccountName`, null, 'Chase Freedom Flex')}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.issuerNameLabel`)}</Label>
            <Input
              value={formData.issuer_name}
              onChange={(e) => setFormData(prev => ({ ...prev, issuer_name: e.target.value }))}
              placeholder={t(`${PP}.placeholderIssuer`, null, 'Chase')}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.balanceFieldLabel`)}</Label>
            <Input
              type="number"
              value={formData.balance}
              onChange={(e) => setFormData(prev => ({ ...prev, balance: e.target.value }))}
              placeholder="3500"
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.aprFieldLabel`)}</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.apr}
              onChange={(e) => setFormData(prev => ({ ...prev, apr: e.target.value }))}
              placeholder="24.99"
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.minimumPaymentLabel`)}</Label>
            <Input
              type="number"
              value={formData.minimum_payment}
              onChange={(e) => setFormData(prev => ({ ...prev, minimum_payment: e.target.value }))}
              placeholder="85"
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.creditLimitLabel`)}</Label>
            <Input
              type="number"
              value={formData.limit}
              onChange={(e) => setFormData(prev => ({ ...prev, limit: e.target.value }))}
              placeholder="5000"
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.dueDateFieldLabel`)}</Label>
            <Input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.statementDateLabel`)}</Label>
            <Input
              type="date"
              value={formData.statement_date}
              onChange={(e) => setFormData(prev => ({ ...prev, statement_date: e.target.value }))}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.accountTypeLabel`)}</Label>
            <select
              value={formData.account_type}
              onChange={(e) => setFormData(prev => ({ ...prev, account_type: e.target.value }))}
              className="h-9 sm:h-10 rounded-md border border-input dark:border-[rgba(142,218,182,0.20)] bg-background dark:bg-[rgba(10,38,28,0.50)] px-3 text-sm text-slate-900 dark:text-[#EAF7F0]"
            >
              <option value="credit_card">{t(`${PP}.accountTypeCreditCard`)}</option>
              <option value="revolving">{t(`${PP}.accountTypeRevolving`)}</option>
              <option value="line_of_credit">{t(`${PP}.accountTypeLineOfCredit`)}</option>
              <option value="personal_loan">{t(`${PP}.accountTypePersonalLoan`)}</option>
            </select>
          </div>
          <div className="col-span-1 sm:col-span-2 flex flex-col gap-1.5 sm:gap-2">
            <Label className="text-xs sm:text-sm">{t(`${PP}.notesLabel`)}</Label>
            <Input
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder={t(`${PP}.placeholderNotes`, null, 'Optional note or limitation')}
              className="h-9 sm:h-10 text-sm"
            />
          </div>
        </div>
        
        <div className="text-[10px] sm:text-xs text-[#5f7468] dark:text-[#93AE9F] leading-relaxed">
          {t(`${PP}.modalAprHint`)}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto h-9 sm:h-10 text-sm">{t(`${PP}.cancel`)}</Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="pp-btn-primary w-full sm:w-auto gap-2 h-9 sm:h-10 text-sm rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18a563]/50 focus-visible:ring-offset-2"
          >
            <Plus size={14} className="sm:w-4 sm:h-4" />
            {isSaving ? t(`${PP}.adding`) : t(`${PP}.addManualAccount`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===================== MAIN COMPONENT =====================
const PaymentPlanner = () => {
  const { t } = useI18n();
  const PP = 'creditBuilder.paymentPlanner';
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [strategies, setStrategies] = useState({});
  const [selectedStrategy, setSelectedStrategy] = useState('avalanche');
  const [budget, setBudget] = useState(1500);
  const [planData, setPlanData] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  
  // Check for CLI Planner return intent
  const returnTo = searchParams.get('returnTo');
  const action = searchParams.get('action');
  const isFromCLIPlanner = returnTo === 'cli-planner';
  
  // Open add modal if came from CLI Planner with action=addCard
  useEffect(() => {
    if (isFromCLIPlanner && action === 'addCard') {
      setAddModalOpen(true);
    }
  }, [isFromCLIPlanner, action]);
  
  // Fetch strategies
  const fetchStrategies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payment-planner/strategies`);
      setStrategies(response.data.strategies || {});
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  }, []);
  
  // Calculate payment plan
  const calculatePlan = useCallback(async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/payment-planner/calculate`, {
        strategy: selectedStrategy,
        budget: budget
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setPlanData(response.data);
    } catch (error) {
      console.error('Error calculating plan:', error);
      toast.error(t(`${PP}.calculateFailed`));
    } finally {
      setCalculating(false);
      setLoading(false);
    }
  }, [selectedStrategy, budget, getAuthHeaders]);
  
  // Add manual account
  const handleAddAccount = async (accountData) => {
    try {
      const response = await axios.post(`${API}/payment-planner/accounts`, accountData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      if (response.data.success) {
        const newCardId = response.data.account?.card_id;
        toast.success(t(`${PP}.accountAddedSuccess`));
        
        // If we came from CLI Planner, return with the new card
        if (isFromCLIPlanner && newCardId) {
          // Navigate back to CLI Planner with the new card selected
          toast.success(t(`${PP}.returningToCli`));
          // Use replace to avoid back button issues
          setTimeout(() => {
            navigate(`/credit-builder/cli-planner?selectCard=${newCardId}&synced=true`, { replace: true });
          }, 500);
        } else {
          calculatePlan();
        }
      }
    } catch (error) {
      console.error('Error adding account:', error);
      toast.error(t(`${PP}.addAccountFailed`));
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);
  
  useEffect(() => {
    if (Object.keys(strategies).length > 0) {
      calculatePlan();
    }
  }, [strategies, calculatePlan]);
  
  // Recalculate when strategy or budget changes
  const handleRecalculate = () => {
    calculatePlan();
  };
  
  if (loading && !planData) {
    return (
      <div className="space-y-4 lg:space-y-6 px-3 sm:px-4 lg:px-0" data-testid="payment-planner-loading">
        <Skeleton className="h-8 lg:h-12 w-48 sm:w-64" />
        <Skeleton className="h-36 lg:h-48 rounded-[20px] lg:rounded-[24px]" />
        <Skeleton className="h-72 lg:h-96 rounded-[20px] lg:rounded-[24px]" />
      </div>
    );
  }
  
  const summary = planData?.summary || {};
  const allocation = planData?.allocation || {};
  const recommendation = planData?.recommendation;
  const projection = planData?.projection || {};
  const bestAlt = planData?.best_alternate;
  const rankedAccounts = planData?.ranked_accounts || [];

  const strategyField = (key, field) =>
    t(`${PP}.strategies.${key}.${field}`, null, strategies[key]?.[field] ?? '');

  return (
    <div className="payment-planner-page space-y-4 lg:space-y-[18px] px-3 sm:px-4 lg:px-0 max-w-full overflow-x-hidden" data-testid="payment-planner">
      {/* CLI Planner Return Flow Banner */}
      {isFromCLIPlanner && (
        <div className="p-4 rounded-[18px] bg-gradient-to-r from-[#e8f8ef] to-[#f4fbf7] border border-[rgba(17,106,71,0.12)] flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-[rgba(39,197,127,0.15)]">
              <ArrowRight size={18} className="text-[#19a56d]" />
            </div>
            <div>
              <div className="text-[14px] font-bold text-[#0b4f35]">{t(`${PP}.cliReturnTitle`)}</div>
              <div className="text-[12px] text-[#5b7666]">{t(`${PP}.cliReturnBody`)}</div>
            </div>
          </div>
          <Button 
            onClick={() => navigate('/credit-builder/cli-planner')}
            variant="outline"
            size="sm"
            className="gap-2 border-[rgba(10,95,62,0.16)]"
          >
            {t(`${PP}.cancelAndReturn`)}
          </Button>
        </div>
      )}
      
      {/* Hero Section */}
      <div className="pp-surface-card p-4 lg:p-6 rounded-[20px] lg:rounded-[24px] bg-white border border-[#dbe8df] shadow-[0_12px_32px_rgba(21,46,31,0.06)]"
        style={{
          background: 'linear-gradient(135deg, rgba(24,165,99,0.06), rgba(214,185,122,0.05)), white'
        }}
      >
        {/* Top Row */}
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 lg:gap-[18px] mb-4 lg:mb-[18px]">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#e4f5eb] text-[#0f7a49] text-xs font-bold uppercase tracking-wider border border-[#cfe7d8] mb-3">
              <CreditCard size={14} />
              {t(`${PP}.badge`)}
            </div>
            <h1 className="pp-heading text-xl sm:text-2xl lg:text-[clamp(28px,3vw,42px)] font-extrabold tracking-tight leading-tight lg:leading-none text-[#15221a]">
              {t(`${PP}.title`)}
            </h1>
            <p className="pp-muted text-xs sm:text-sm lg:text-[15px] text-[#5f7468] mt-2 lg:mt-2.5 leading-relaxed max-w-[840px]">
              {t(`${PP}.subtitle`)}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 w-full lg:w-auto">
            <div className="flex-1 lg:flex-none lg:min-w-[270px] p-3 lg:p-3.5 rounded-[16px] lg:rounded-[18px] border border-[#dbe8df] bg-white shadow-[0_8px_22px_rgba(21,46,31,0.04)]">
              <label className="block text-xs text-[#5f7468] font-semibold mb-2">{t(`${PP}.monthlyBudget`)}</label>
              <div className="flex items-center gap-2">
                <span className="text-lg lg:text-xl text-[#0f7a49] font-extrabold">$</span>
                <input
                  type="number"
                  min="0"
                  step="25"
                  value={budget}
                  onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
                  className="w-full border-0 outline-none bg-transparent text-lg lg:text-[22px] font-extrabold tracking-tight text-[#15221a]"
                  data-testid="budget-input"
                />
              </div>
            </div>
            <Button
              onClick={handleRecalculate}
              variant="outline"
              className="h-10 lg:h-auto px-4 rounded-[14px] lg:rounded-[16px] border-[#dbe8df] font-bold"
              disabled={calculating}
              data-testid="recalculate-btn"
            >
              <RefreshCw size={16} className={calculating ? 'animate-spin' : ''} />
              <span className="ml-2 sm:hidden">{t(`${PP}.recalculate`)}</span>
            </Button>
          </div>
        </div>
        
        {/* Strategy Selector */}
        <StrategySelector
          strategies={strategies}
          selected={selectedStrategy}
          onSelect={(key) => {
            setSelectedStrategy(key);
            // Auto-recalculate after a short delay
            setTimeout(() => calculatePlan(), 100);
          }}
        />
        
        {/* Summary + AI Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_0.85fr] gap-4 lg:gap-[18px] mt-4 lg:mt-[18px]">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2 lg:gap-3">
            <SummaryCard
              label={t(`${PP}.totalDebt`)}
              value={formatCurrency(summary.total_debt)}
              subtitle={t(`${PP}.totalDebtSubtitle`)}
            />
            <SummaryCard
              label={t(`${PP}.availableBudget`)}
              value={formatCurrency(budget)}
              subtitle={t(`${PP}.availableBudgetSubtitle`)}
              variant="green"
            />
            <SummaryCard
              label={t(`${PP}.selectedStrategy`)}
              value={strategyField(selectedStrategy, 'label') || selectedStrategy}
              subtitle={t(`${PP}.selectedStrategySubtitle`)}
              variant="gold"
            />
            <SummaryCard
              label={t(`${PP}.accountsPaid`)}
              value={summary.accounts_paid_this_month || 0}
              subtitle={t(`${PP}.accountsPaidSubtitle`)}
              variant="green"
            />
            <SummaryCard
              label={t(`${PP}.interestSaved`)}
              value={formatCurrency(summary.interest_saved, 2)}
              subtitle={t(`${PP}.interestSavedSubtitle`)}
              variant="gold"
            />
            <SummaryCard
              label={t(`${PP}.utilImprovement`)}
              value={t(`${PP}.pointsShort`, { count: (summary.utilization_improvement || 0).toFixed(1) }, `${(summary.utilization_improvement || 0).toFixed(1)} pts`)}
              subtitle={t(`${PP}.utilImprovementSubtitle`)}
              variant="green"
            />
          </div>
          
          {/* AI Recommendation */}
          <AIRecommendationPanel
            recommendation={recommendation}
            strategies={strategies}
            selectedStrategy={selectedStrategy}
          />
        </div>
        
        {/* Warning Bar */}
        {allocation.warning && (
          <div className="mt-4 p-3.5 rounded-[16px] bg-[#fce9e9] border border-[#f1caca] text-[#ab4343] text-[13px] font-semibold leading-relaxed flex items-start gap-2">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            {allocation.underfunded
              ? t(`${PP}.warnings.budgetBelowMinimums`, {
                  budget: formatCurrency(budget),
                  minimums: formatCurrency(allocation.total_minimums),
                })
              : allocation.warning}
          </div>
        )}
      </div>
      
      {/* Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.18fr_0.92fr] gap-4 lg:gap-[18px]">
        {/* Left Column - Ranked Order */}
        <div className="pp-surface-card p-4 lg:p-5 rounded-[20px] lg:rounded-[24px] bg-white border border-[#dbe8df] shadow-[0_12px_32px_rgba(21,46,31,0.06)]">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4">
            <div>
              <h2 className="pp-heading text-lg lg:text-[21px] font-extrabold tracking-tight text-[#15221a]">{t(`${PP}.recommendedPaymentOrder`)}</h2>
              <p className="text-xs lg:text-sm text-[#5f7468] mt-1.5 leading-relaxed">
                {t(`${PP}.recommendedPaymentOrderSubtitle`)}
              </p>
            </div>
          </div>
          
          {/* Strategy Explanation */}
          <div className="p-4 rounded-[18px] bg-gradient-to-br from-[#eef8f2] to-[#fbf7ee] border border-[#dce9df] mb-4">
            <div className="text-xs uppercase tracking-wider text-[#0f7a49] font-extrabold mb-2">
              {strategyField(selectedStrategy, 'label')}{t(`${PP}.methodSuffix`)}
            </div>
            <p className="text-sm pp-body text-[#15221a] leading-relaxed">
              {strategyField(selectedStrategy, 'explain')}
            </p>
          </div>
          
          {/* Account Cards */}
          <div className="space-y-3.5">
            {rankedAccounts.length === 0 ? (
              <div className="pp-empty-state p-8 rounded-[20px] border border-dashed border-[#cfe3d8] bg-[#f8fcfa] text-center">
                <DollarSign size={48} className="mx-auto text-stone-300 dark:text-[#5f7468] mb-3" />
                <h3 className="pp-heading font-semibold mb-1">{t(`${PP}.noDebtAccounts`)}</h3>
                <p className="text-sm pp-muted text-stone-500 mb-4">{t(`${PP}.addFromUtilization`)}</p>
                <Button
                  onClick={() => setAddModalOpen(true)}
                  className="pp-btn-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18a563]/50 focus-visible:ring-offset-2"
                >
                  <Plus size={16} className="mr-2" />
                  {t(`${PP}.addManualAccount`)}
                </Button>
              </div>
            ) : (
              rankedAccounts.map((account) => (
                <AccountCard
                  key={account.card_id}
                  account={account}
                  rank={account.rank}
                  strategyKey={selectedStrategy}
                />
              ))
            )}
          </div>
        </div>
        
        {/* Right Column - Payment Plan Table + Add Account */}
        <div className="space-y-4 lg:space-y-[18px]">
          {/* Monthly Payment Plan Table */}
          <div className="pp-surface-card p-4 lg:p-5 rounded-[20px] lg:rounded-[24px] bg-white border border-[#dbe8df] shadow-[0_12px_32px_rgba(21,46,31,0.06)]">
            <div className="mb-4">
              <h2 className="pp-heading text-lg lg:text-[21px] font-extrabold tracking-tight text-[#15221a]">{t(`${PP}.monthlyPaymentPlan`)}</h2>
              <p className="text-xs lg:text-sm text-[#5f7468] mt-1.5 leading-relaxed">
                {t(`${PP}.monthlyPaymentPlanSubtitle`)}
              </p>
            </div>
            
            <div className="overflow-x-auto border border-[#dbe8df] rounded-[16px] lg:rounded-[20px] bg-white">
              <table className="w-full min-w-[600px] lg:min-w-[760px]" data-testid="plan-table">
                <thead>
                  <tr>
                    {['rank', 'account', 'minimum', 'extra', 'total', 'after', 'reason'].map((key) => (
                      <th key={key} className="pp-table-head text-left p-3 bg-[#f4f8f5] text-[#5f7468] text-xs uppercase tracking-wider font-bold border-b border-[#dbe8df]">
                        {t(`${PP}.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rankedAccounts.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-4 text-center text-stone-500">{t(`${PP}.noAccountsYet`)}</td>
                    </tr>
                  ) : rankedAccounts.map((account) => (
                    <tr key={account.card_id} className="pp-table-row border-b border-[#edf3ef] hover:bg-[#f8fbf9]">
                      <td className="p-3 text-sm font-bold">#{account.rank}</td>
                      <td className="p-3">
                        <div className="font-semibold text-sm">{account.account_name}</div>
                        <div className="text-xs text-[#8a9b91]">{account.issuer_name || t(`${PP}.issuerNotSet`)}</div>
                      </td>
                      <td className="p-3 text-sm">{formatCurrency(account.minimum)}</td>
                      <td className="p-3 text-sm font-semibold text-[#0f7a49]">{formatCurrency(account.extra)}</td>
                      <td className="p-3 text-sm font-bold">{formatCurrency(account.total_payment)}</td>
                      <td className="p-3 text-sm">{formatCurrency(account.balance_after)}</td>
                      <td className="p-3 text-xs text-[#5f7468]">{account.reason_tag}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <p className="text-[13px] text-[#5f7468] mt-3 leading-relaxed">
              {allocation.underfunded
                ? t(`${PP}.budgetUnderMinimums`)
                : t(`${PP}.minimumsExtraBudget`, {
                    minimums: formatCurrency(allocation.total_minimums),
                    extra: formatCurrency(allocation.extra_budget),
                  })
              }
            </p>
          </div>
          
          {/* Add Manual Account Section */}
          <div className="pp-surface-card p-4 lg:p-5 rounded-[20px] lg:rounded-[24px] bg-white border border-[#dbe8df] shadow-[0_12px_32px_rgba(21,46,31,0.06)]">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4">
              <div>
                <h2 className="pp-heading text-lg lg:text-[21px] font-extrabold tracking-tight text-[#15221a]">{t(`${PP}.addManualDebt`)}</h2>
                <p className="text-xs lg:text-sm text-[#5f7468] mt-1.5 leading-relaxed">
                  {t(`${PP}.addManualDebtSubtitle`)}
                </p>
              </div>
            </div>
            
            <Button
              onClick={() => setAddModalOpen(true)}
              className="pp-btn-primary w-full h-10 lg:h-12 rounded-[14px] lg:rounded-[16px] text-sm lg:text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18a563]/50 focus-visible:ring-offset-2"
              data-testid="add-manual-btn"
            >
              <Plus size={16} className="mr-2" />
              {t(`${PP}.addManualAccount`)}
            </Button>
            
            <p className="text-[10px] lg:text-xs text-[#5f7468] mt-3 leading-relaxed">
              {t(`${PP}.missingAprHint`)}
            </p>
          </div>
        </div>
      </div>
      
      {/* Projection Snapshot */}
      <div className="pp-surface-card p-4 lg:p-5 rounded-[20px] lg:rounded-[24px] bg-white border border-[#dbe8df] shadow-[0_12px_32px_rgba(21,46,31,0.06)]">
        <div className="mb-4">
          <h2 className="pp-heading text-lg lg:text-[21px] font-extrabold tracking-tight text-[#15221a]">{t(`${PP}.projectionSnapshot`)}</h2>
          <p className="text-xs lg:text-sm text-[#5f7468] mt-1.5 leading-relaxed">
            {t(`${PP}.projectionSnapshotSubtitle`)}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-3.5">
          <div className="p-4 rounded-[18px] border border-[#dbe8df] bg-white shadow-[0_8px_22px_rgba(21,46,31,0.04)]">
            <h4 className="text-xs uppercase tracking-wider text-[#8a9b91] font-extrabold mb-2.5">{t(`${PP}.projectedPayoffTime`)}</h4>
            <div className="text-[28px] font-black tracking-tight text-[#15221a]">
              {projection.payoff_possible
                ? t(`${PP}.monthsShort`, { count: projection.months }, `${projection.months} mo`)
                : t(`${PP}.budgetTooLow`)}
            </div>
            <div className="text-[13px] text-[#5f7468] mt-2 leading-relaxed">
              {projection.payoff_possible
                ? t(`${PP}.payoffTimeline`, {
                    strategy: strategyField(selectedStrategy, 'label'),
                    budget: formatCurrency(budget),
                  })
                : t(`${PP}.increaseBudgetHint`)
              }
            </div>
          </div>
          
          <div className="p-4 rounded-[18px] border border-[#dbe8df] bg-white shadow-[0_8px_22px_rgba(21,46,31,0.04)]">
            <h4 className="text-xs uppercase tracking-wider text-[#8a9b91] font-extrabold mb-2.5">{t(`${PP}.projectedTotalInterest`)}</h4>
            <div className="text-[28px] font-black tracking-tight text-[#15221a]">
              {projection.total_interest !== null ? formatCurrency(projection.total_interest, 2) : '—'}
            </div>
            <div className="text-[13px] text-[#5f7468] mt-2 leading-relaxed">
              {t(`${PP}.missingAprPrecision`)}
            </div>
          </div>
          
          <div className="p-4 rounded-[18px] border border-[#dbe8df] bg-white shadow-[0_8px_22px_rgba(21,46,31,0.04)]">
            <h4 className="text-xs uppercase tracking-wider text-[#8a9b91] font-extrabold mb-2.5">{t(`${PP}.nextBestAlternate`)}</h4>
            <div className="text-[28px] font-black tracking-tight text-[#15221a]">
              {bestAlt?.strategy
                ? strategyField(bestAlt.strategy, 'label')
                : bestAlt?.strategy_info?.label || '—'}
            </div>
            <div className="text-[13px] text-[#5f7468] mt-2 leading-relaxed">
              {bestAlt ? (
                <>
                  {bestAlt.strategy
                    ? strategyField(bestAlt.strategy, 'subtitle')
                    : bestAlt.strategy_info?.subtitle}. 
                  {bestAlt.months_diff === 0
                    ? ` ${t(`${PP}.samePayoffTime`)}`
                    : ` ${t(bestAlt.months_diff > 0 ? `${PP}.monthsSlower` : `${PP}.monthsFaster`, { count: Math.abs(bestAlt.months_diff) })}`}
                  ,
                  {bestAlt.interest_diff === 0
                    ? ` ${t(`${PP}.sameInterest`)}`
                    : ` ${t(bestAlt.interest_diff > 0 ? `${PP}.moreInterest` : `${PP}.lessInterest`, { amount: formatCurrency(Math.abs(bestAlt.interest_diff), 2) })}`}
                  .
                </>
              ) : t(`${PP}.noAlternateComparison`)}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add Account Modal */}
      <AddAccountModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddAccount}
      />
    </div>
  );
};

export default PaymentPlanner;
